/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Redirect, Route, useLocation, useParams } from "react-router-dom";
import {
  getUserLocale,
  initializeLocale,
  setUserLocale,
  saveUserSettings,
} from "utilities/UserSettingsUtilities";
import AppLoading from "components/AppLoading";
import { PrerenderedComponent } from "react-prerendered-component";
import { match } from "ts-pattern";
import { LocaleContext, useLocale } from "components/LocaleContext";
import { useCurrentUser } from "actions/CurrentUserActions";
import { localeShorthands } from "constants/Locales";

export const LocaleMatcher: React.FC = ({ children }) => {
  return (
    <>
      <Route path="/:locale">
        <LocaleChecker>{children}</LocaleChecker>
      </Route>

      <Route exact path="/" component={RedirectToLocaleHome} />
    </>
  );
};

const LocaleChecker: React.FC = ({ children }) => {
  const { locale } = useParams<{ locale: string }>();
  const location = useLocation();

  const isValidLocale = Object.values(localeShorthands).includes(locale);
  const [isLoadingLocale, setIsLoadingLocale] = useState(true);

  useEffect(() => {
    if (isValidLocale) {
      setIsLoadingLocale(true);
      setUserLocale(locale);
      initializeLocale().then(() => setIsLoadingLocale(false));
    }
  }, [isValidLocale, locale]);

  // Redirect to default locale if invalid, i.e. if a user lands on podcast.adobe.com/enhance

  if (!isValidLocale) {
    const userLocale = getUserLocale() ?? "en";
    const localeShorthand = localeShorthands[userLocale];
    return (
      <Redirect
        to={{
          pathname: `/${localeShorthand}${location.pathname}`,
          search: location.search,
          hash: location.hash,
        }}
      />
    );
  }

  return (
    <PrerenderedComponent live={!isLoadingLocale}>
      {match(isLoadingLocale)
        .with(true, () => <AppLoading />)
        .otherwise(() => (
          <LocaleContext.Provider value={locale}>
            <UserSettings />
            {children}
          </LocaleContext.Provider>
        ))}
    </PrerenderedComponent>
  );
};

const UserSettings = (): React.ReactElement => {
  const { currentUser } = useCurrentUser();
  const locale = useLocale();
  const lastSetLocale = useRef<string | null>(null);
  useEffect(() => {
    // This will only fire if the user is loaded and the locale is set
    if (currentUser && locale !== lastSetLocale.current) {
      saveUserSettings({ locale })
        .then(() => (lastSetLocale.current = locale))
        .catch((error) => {
          console.error("Error setting user locale:", error);
          newrelic.noticeError(error);
        });
    }
  }, [currentUser, locale]);

  return <></>;
};

const RedirectToLocaleHome: React.FC = () => {
  const userLocale = getUserLocale() ?? "en";
  const localeShorthand = localeShorthands[userLocale];
  const location = useLocation();

  return (
    <Redirect
      to={{ pathname: `/${localeShorthand}`, search: location.search }}
    />
  );
};
